import { useRef } from 'react'
import type { ReactNode } from 'react'
import styles from './Tabs.module.css'

export interface TabItem {
    id: string
    label: ReactNode
}

interface TabsProps {
    items: TabItem[]
    active: string
    onChange: (id: string) => void
    label: string
    children: ReactNode
}

/**
 * Proper tablist semantics with a roving tabindex and arrow-key navigation.
 * The old tab bars were <button> inside <ul> with no roles at all, and misused
 * aria-current="page" in place of aria-selected.
 */
export default function Tabs({ items, active, onChange, label, children }: TabsProps) {
    const refs = useRef<Record<string, HTMLButtonElement | null>>({})

    function onKeyDown(e: React.KeyboardEvent) {
        const i = items.findIndex((t) => t.id === active)
        if (i < 0) return
        let next = i
        if (e.key === 'ArrowRight') next = (i + 1) % items.length
        else if (e.key === 'ArrowLeft') next = (i - 1 + items.length) % items.length
        else if (e.key === 'Home') next = 0
        else if (e.key === 'End') next = items.length - 1
        else return
        e.preventDefault()
        const id = items[next].id
        onChange(id)
        refs.current[id]?.focus()
    }

    return (
        <>
            <div role="tablist" aria-label={label} className={styles.list} onKeyDown={onKeyDown}>
                {items.map((tab) => {
                    const selected = tab.id === active
                    return (
                        <button
                            key={tab.id}
                            ref={(el) => { refs.current[tab.id] = el }}
                            role="tab"
                            id={`tab-${tab.id}`}
                            aria-selected={selected}
                            aria-controls={`panel-${tab.id}`}
                            tabIndex={selected ? 0 : -1}
                            className={selected ? styles.tabActive : styles.tab}
                            onClick={() => onChange(tab.id)}
                        >
                            {tab.label}
                        </button>
                    )
                })}
            </div>
            <div role="tabpanel" id={`panel-${active}`} aria-labelledby={`tab-${active}`} tabIndex={0}>
                {children}
            </div>
        </>
    )
}
